import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import xlsx from 'json-as-xlsx';
import { getISOWeeksInYear, getWeek } from 'date-fns';

import { useToggle } from 'customHooks/useToggle';
import Overlay from 'lib/Overlay';
import Button, { Color } from 'lib/Button';
import { Store } from 'components/common/types/Store.types';
import { formatExportDateAndTime } from 'utils/formatExportDateAndTime';
import downloadSvg from 'assets/icons/download.svg';
import { getOOCFramesCount } from 'store/pages/directSalesOOC/selectors';
import { OOCReportButton } from 'components/pages/Deals/utils/OOC/OOCReportButton';
import { OOCReportTable } from './OOCReportTable/OOCReportTable';
import { REPORT_COLUMNS, REPORT_SHEET_NAME } from './consts';
import { getTableContent } from './utils';

const OOCReport = (): React.ReactElement => {
  const { isOpen, open, close } = useToggle(false);
  const campaignsWithOOCFrames = useSelector((state: Store) => state.directSalesOOC.campaignsWithOOCFrames);
  const oocFramesCount = useSelector(getOOCFramesCount);

  const getIndex = ({
    index,
    week,
    year,
    weeksInYear,
  }: {
    index: number;
    week: number;
    year: number;
    weeksInYear: number;
  }): { label: string; value: string } => {
    const weekIdx = week + index;

    if (weekIdx > weeksInYear) {
      const nextYear = year + 1;
      const nextYearWeek = weekIdx % weeksInYear;

      return {
        label: `Week ${nextYearWeek} ${nextYear}`,
        value: `${nextYearWeek}${nextYear}`,
      };
    }

    return {
      label: `Week ${weekIdx} ${year}`,
      value: `${weekIdx}${year}`,
    };
  };

  const getWeekColumns = useCallback(() => {
    const date = new Date();
    const week = getWeek(date);
    const year = date.getFullYear();
    const weeksInYear = getISOWeeksInYear(date);

    return Array.from({ length: weeksInYear }, (_, index) => {
      return getIndex({ index, week, year, weeksInYear });
    });
  }, [getIndex]);

  const exportTable = useCallback(
    (date: Date) => {
      xlsx(
        [
          {
            sheet: REPORT_SHEET_NAME,
            columns: [...REPORT_COLUMNS, ...getWeekColumns()],
            content: getTableContent(campaignsWithOOCFrames),
          },
        ],
        { fileName: `${REPORT_SHEET_NAME}_${formatExportDateAndTime(date)}` },
      );
    },
    [getWeekColumns, campaignsWithOOCFrames],
  );

  return (
    <>
      <OOCReportButton oocFramesCount={oocFramesCount} onClick={open} />
      <Overlay
        isOpen={isOpen}
        onClose={close}
        header={
          <header className="w-full flex justify-between items-center pr-6">
            <h3 className="sub-header-lg">Out of charge frames</h3>
            <Button color={Color.BLACK_AND_WHITE} onClick={() => exportTable(new Date())}>
              <SVG src={downloadSvg} className="fill-current w-3.5" />
              Export table
            </Button>
          </header>
        }
        childrenExtraStyles="rounded-md"
      >
        <OOCReportTable />
      </Overlay>
    </>
  );
};

export default OOCReport;
